import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';

import { NumberField } from '../fields/widgets/number-field';
import { SelectField } from '../fields/widgets/select-field';

const OBJECT_FIT_OPTIONS = [
  { label: 'Cover', value: 'cover' },
  { label: 'Contain', value: 'contain' },
  { label: 'Fill', value: 'fill' },
  { label: 'None', value: 'none' },
] as const;

type ObjectFit = typeof OBJECT_FIT_OPTIONS[number]['value'];

export type ImageProps = {
  src: string;
  alt: string;
  width: string;
  height: string;
  objectFit: ObjectFit;
  borderRadius: string;
};

/** Placeholder shown in the editor before an image URL has been set. */
export function ImagePlaceholder({
  width = '100%',
  height = 200,
  borderRadius,
}: {
  width?: string;
  height?: number | string;
  borderRadius?: string;
}): JSX.Element {
  return (
    <div
      style={{
        width,
        height,
        background: 'var(--dcm-cms-image-placeholder-bg, #f0f2f4)',
        borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dcm-cms-image-placeholder-color, #646d72)',
        fontSize: 14,
      }}
    >
      No image URL set
    </div>
  );
}

/** Plain image block: a URL plus sizing/fit controls. */
export const imageConfig: ComponentConfig<ImageProps> = {
  label: 'Image',
  ai: {
    instructions:
      'Displays an image from a URL. Use for hero images, illustrations, and media. Always provide alt text for accessibility.',
  },
  fields: {
    src: {
      type: 'text',
      label: 'Image URL',
      ai: { stream: false, instructions: 'Full image URL. Do not stream partial URLs.' },
    },
    alt: {
      type: 'text',
      label: 'Alt Text',
      ai: { instructions: 'Descriptive alt text for screen readers.' },
    },
    width: { type: 'text', label: 'Width' },
    height: { type: 'text', label: 'Height' },
    objectFit: {
      type: 'custom',
      label: 'Object Fit',
      render: SelectField(OBJECT_FIT_OPTIONS),
      ai: {
        schema: { type: 'string', enum: ['cover', 'contain', 'fill', 'none'] },
      },
    },
    borderRadius: {
      type: 'custom',
      label: 'Border Radius',
      render: NumberField('px'),
      ai: {
        schema: { type: 'string', pattern: '^\\d+px$' },
        instructions: 'CSS pixel value, e.g. 8px',
      },
    },
  },
  defaultProps: {
    src: '',
    alt: '',
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
    borderRadius: '0px',
  },
  render: ({ src, alt, width, height, objectFit, borderRadius }) => {
    if (!src) {
      return (
        <ImagePlaceholder
          // An empty width means "unset", so fall back to the placeholder's own default.
          width={width === '' ? undefined : width}
          height={height === 'auto' ? 200 : height}
          borderRadius={borderRadius}
        />
      );
    }

    return (
      <img
        src={src}
        alt={alt}
        loading='lazy'
        style={{
          display: 'block',
          width,
          height,
          objectFit,
          borderRadius,
        }}
      />
    );
  },
};
